import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { FaPlus, FaCalendar } from 'react-icons/fa';
import { BillType } from '../../../../../../types';
import { createLocalBill } from '../../../../../../../../../../crud/management_api/local_bills';
import Calendar from 'react-calendar';
import './CreateBillModal.scss'; // Asegúrate de tener este archivo CSS
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';



interface CreateBillModalProps {
  show: boolean;
  handleClose: () => void;
  handleAddBill: (bill: BillType) => void;
  globalCategoriesList: any[];   // Lista de categorías globales
  localCategoriesList: any[];    // Lista de categorías locales
}

const CreateBillModal: React.FC<CreateBillModalProps> = ({
  show,
  handleClose,
  handleAddBill,
  globalCategoriesList,
  localCategoriesList
}) => {
  const [newBillName, setNewBillName] = useState('');
  const [newBillAmount, setNewBillAmount] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [newBillNumber, setNewBillNumber] = useState('');

  const [selectedCategory, setSelectedCategory] = useState<{ id: number, name: string, type: 'local' | 'global' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (!show) {
      setNewBillName('');
      setNewBillAmount('');
      setSelectedDate(null);
      setFiles([]);
      setSelectedCategory(null);
      setNewBillNumber('');
      setLoading(false);
    }
  }, [show]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(selectedFiles);
    }
  };

  const renderFilePreviews = () => {
    return files.map((file, index) => {
      const isImage = file.type.startsWith('image');
      return (
        <div key={index} className="file-preview">
          {isImage ? (
            <div className="file-preview-text">
              <i className="file-preview-icon">🖼️</i> {file.name}
            </div>
          ) : (
            <div className="file-preview-text">
              <i className="file-preview-icon">📄</i> {file.name}
            </div>
          )}
        </div>
      );
    });
  };

  const combinedCategories = [
    ...localCategoriesList.map(cat => ({ id: cat.local_bill_category_id, name: cat.category_name, type: 'local' as const })),
    ...globalCategoriesList.map(cat => ({ id: cat.global_bill_category_id, name: cat.category_name, type: 'global' as const })),
  ];

  const handleSubmit = async () => {
    if (!newBillName || !newBillAmount || !selectedDate || !selectedCategory) {
      // alert("Por favor completa todos los campos requeridos.");
      toast.error("Por favor completa todos los campos requeridos.");
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user.local_id;
    if (!localId) {
      // alert("No se encontró el ID del local en el localStorage.");
      toast.error("No se encontró el ID del local en el localStorage.");
      return;
    }

    setLoading(true);

    try {
      const { id, name, type } = selectedCategory;
      let local_bill_category_id: number | null = null;
      let global_bill_category_id: number | null = null;
      let global_bill_category_name: string | null = null;
      let local_bill_category_name: string | null = null;

      if (type === 'local') {
        local_bill_category_id = id;
        local_bill_category_name = name;
      } else {
        global_bill_category_id = id;
        global_bill_category_name = name;
      }

      const payload = {
        local_id: localId,
        local_bill_category_id: local_bill_category_id,
        global_bill_category_id: global_bill_category_id,
        global_bill_category_name: global_bill_category_name,
        local_bill_category_name: local_bill_category_name,
        amount: parseFloat(newBillAmount),
        bill_name: newBillName,
        bill_number: newBillNumber ? parseInt(newBillNumber, 10) : 0,
        bill_date: selectedDate.toISOString().split('T')[0], // Formatear la fecha como YYYY-MM-DD
        file: files.length > 0 ? files[0].name : null, // Solo el nombre del archivo como ejemplo
      };

      console.log("Enviando payload a la API createLocalBill:", payload);
      const response = await createLocalBill(payload);

      if (response && response.data) {
        handleAddBill(response.data);
        toast.success("Pago creado con éxito");

        // Limpiamos los campos
        setNewBillName('');
        setNewBillAmount('');
        setSelectedDate(null);
        setFiles([]);
        setSelectedCategory(null);
        setNewBillNumber('');

        handleClose();
      } else {
        console.error("Respuesta inesperada del backend:", response);
        //alert("Error al crear la factura.");
        toast.error("Error al crear la factura.");
      }
    } catch (error) {
      console.error("Error al crear el bill:", error);
      // alert("Ocurrió un error al crear el pago.");
      toast.error("Ocurrió un error al crear el pago.");
    } finally {
      setLoading(false);
    }
  };

  const toggleCalendar = () => {
    setShowCalendar((prev) => !prev);
  };

  return (
    <Modal className='modal-create-bill' show={show} onHide={handleClose} centered dialogClassName="create-bill-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            <span>Crear Nuevo Pago</span>
          </Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form className="add-bill-form mb-4">
          <h5>Detalles del Pago</h5>

          <Form.Group className="mp-formgroup horizontal" controlId="billName">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nombre del pago"
              value={newBillName}
              onChange={(e) => setNewBillName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mp-formgroup horizontal" controlId="billAmount">
            <Form.Label>Monto</Form.Label>
            <Form.Control
              type="number"
              placeholder="Monto a pagar"
              value={newBillAmount}
              onChange={(e) => setNewBillAmount(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="dob" className="mp-formgroup horizontal fecha">
            <Form.Label>Fecha</Form.Label>
            <Button
              variant="no"
              onClick={toggleCalendar}
              className="boton-show-calendar"
            >
              {selectedDate
                ? selectedDate.toLocaleDateString()
                : ""} <FaCalendar />
            </Button>
          </Form.Group>
          <div className="calendar-container calendario-create-user">
            {showCalendar && (
              <div className='mp-form-calendar-container'>
                <Calendar
                  onChange={(date) => {
                    setSelectedDate(date as Date);
                    setShowCalendar(false);
                  }}
                  value={selectedDate}
                  locale="es-ES"
                  className="mp-form-calendar"
                />
              </div>
            )}
          </div>

          <Form.Group className="mp-formgroup horizontal" controlId="billNumber">
            <Form.Label>Número de Factura</Form.Label>
            <Form.Control
              type="number"
              placeholder="Número de factura"
              value={newBillNumber}
              onChange={(e) => setNewBillNumber(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mp-formgroup horizontal categoria" controlId="billCategory">
            <Form.Label>Cuenta</Form.Label>
            <Dropdown className="mp-dropdown" style={{zIndex:1}}>
              <Dropdown.Toggle variant="none" id="dropdown-category">
                {selectedCategory ? selectedCategory.name : 'Seleccionar cuenta'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {combinedCategories.map((category) => (
                  <Dropdown.Item
                    key={`${category.id}-${category.type}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category.name} 
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          {/* <Form.Group className="mediopunto-multi-file-upload" controlId="billFiles">
            <Form.Label>Archivos</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
            />
            {renderFilePreviews()}
          </Form.Group> */}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="none" className="form-button-cancel" onClick={handleClose}>
          Cerrar
        </Button>
        <Button variant="none" className="form-button-create" onClick={handleSubmit} disabled={loading}>
          {loading ? "Creando..." : <><FaPlus /> Crear Pago</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateBillModal;
